import { action, makeObservable, observable } from "mobx";
import {
	fetchProtectedResource,
	fetchResource,
	isNumeric,
} from "../../utilities";
import { message } from "antd";
import { isArray } from "lodash";
import BaseService from "../base/BaseService";
import {
	QUOTE_CANCEL_URL,
	QUOTE_POLICIES_URL,
	QUOTE_CALCULATE_CANCEL_URL,
} from "../../configs/api-paths";

/**
 * Service dealing with policies reporting.
 */
class PoliciesService extends BaseService {
	/**
	 * All policies.
	 * @type {[]}
	 */
	@observable
	policiesData = [];

	/**
	 * Is the service loading data.
	 * @type {boolean}
	 */
	@observable
	loading = false;

	/**
	 * Policies table pagination object.
	 * @type {{}}
	 */
	@observable
	pagination = {};

	/**
	 * Policies table sorting object.
	 * @type {{}}
	 */
	@observable
	sorter = {
		field: "renewalDate",
		order: "descend",
	};

	@observable
	search = {};

	@observable
	cancellationModalVisible = false;

	@observable
	cancellationModalRecord;

	@observable isViewMode = false;

	constructor(props) {
		super(props);
		makeObservable(this);
	}

	@action
	setIsViewMode = (value) => {
		this.isViewMode = value;
	};

	@action
	setPoliciesData = (data) => {
		this.policiesData = data;
	};

	@action
	setLoading = (loading) => {
		this.loading = loading;
	};

	@action
	setPagination = (pagination) => {
		this.pagination = pagination;
	};

	@action
	setSorter = (sorter) => {
		this.sorter = sorter;
	};

	@action
	setSearch = (search) => {
		// Check if the search value has changed
		if (search.value !== this.search.value) {
			// If the search value has changed, reset the current page to 1
			this.pagination = {
				...this.pagination,
				current: 1,
			};
		}
		// Update the search object
		this.search = search;
	};



	@action
	showCancellationModal = () => {
		this.cancellationModalVisible = true;
	};

	@action
	hideCancellationModal = () => {
		this.cancellationModalVisible = false;
	};

	@action
	setCancellationModalRecord = (record) => {
		this.cancellationModalRecord = record;
	};

	/**
	 * @returns {Promise<void>}
	 */
	loadPoliciesData = async () => {
		this.setLoading(true);
		let hideLoadingMessage = message.loading("Loading...", 0);

		try {
			let field = this.sorter.field;
			const order = this.sorter.order;

			const currentPage = this.pagination.current;
			const pageSize = this.pagination.pageSize;

			let url = `${QUOTE_POLICIES_URL}?`;

			if (field && order) {
				if (field === "first_name") {
					field = "organisation.firstName";
				}

				if (field === "last_name") {
					field = "organisation.lastName";
				}

				if (field === "phone") {
					field = "organisation.phone";
				}

				if (field === "address1") {
					field = "organisation.address1";
				}

				if (field === "address2") {
					field = "organisation.address2";
				}

				if (field === "city") {
					field = "organisation.city";
				}

				if (field === "post_code") {
					field = "organisation.postcode";
				}

				url += `sort=policyNumber,${order === "ascend" ? "asc" : "desc"}&`;
			}

			if (isNumeric(currentPage) && isNumeric(pageSize)) {
				url += `page=${currentPage - 1}&size=${pageSize}&`;
			}


			if (isArray(this.search.fields)) {
				const _searchFields = this.search.fields.map((field) => {
					let _field = field;
					if (field === "organisation.first_name") {
						_field = "organisation.firstName";
					}
					if (field === "organisation.last_name") {
						_field = "organisation.lastName";
					}
					if (field === "organisation.post_code") {
						_field = "organisation.postcode";
					}
					return _field;
				});
				// url += `searchFields=${this.search.fields.join(",")}&`;
				url += `searchFields=${_searchFields.join(",")}&`;
				url += `searchValue=${this.search.value}&`;
			}

			const result = await fetchProtectedResource(url);
			hideLoadingMessage();
			const pagedResults = await result.json();

			this.setPagination({
				...this.pagination,
				total: pagedResults.totalElements,
			});
			this.setPoliciesData(pagedResults.content);

		} catch (error) {
			console.log(error);

			hideLoadingMessage();
			message.error(
				`There was error while trying to fetch policies data from the server. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};

	calculateQuoteCancellation = async (cancellationDate, quoteId) => {
		this.setLoading(true);

		try {
			let url = `${QUOTE_CALCULATE_CANCEL_URL.replace("{id}", quoteId)}`;

			let result = await fetchResource(url, {
				method: "PUT",
				body: {
					cancellationDate,
				},
			});

			let cancellationCalculation = await result.json();

			this.setCancellationModalRecord({
				...this.cancellationModalRecord,
				...cancellationCalculation,
			});
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to cancel this quote. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};

	cancelQuote = async (cancellationReason, cancellationDate) => {
		this.setLoading(true);

		try {
			let url = `${QUOTE_CANCEL_URL.replace(
				"{id}",
				this.cancellationModalRecord.quoteId
			)}`;

			await fetchResource(url, {
				method: "PUT",
				body: {
					cancellationReason,
					cancellationDate,
				},
			});
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to cancel this quote. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};
}

export default PoliciesService;
